import React from 'react';
import { motion } from 'framer-motion';

const TechRecruitingAgency = ({
  title,
  description,
  problemTitle,
  problemItems = [],
  solutionTitle,
  solutionDescription,
  solutionPoints = [],
  ctaText = "Find Your Talent Today"
}) => {
  const containerAnim = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1 } },
  };

  const itemAnim = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.5 } },
  };

  return (
    <section
      className="py-24 px-6 md:px-12 relative overflow-hidden"
      style={{ backgroundColor: '#001534' }}
    >
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 opacity-5">
        <div className="absolute top-20 right-20 w-96 h-96 rounded-full border-2 border-cyan-400" />
        <div className="absolute top-40 right-60 w-64 h-64 rounded-full border-2 border-blue-400" />
      </div>
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 opacity-5">
        <div className="absolute bottom-20 left-20 w-80 h-80 rounded-full border-2 border-cyan-400" />
      </div>

      <div className="max-w-[1240px] mx-auto relative z-10">
        {/* Header */}
        <motion.div
          className="text-center max-w-4xl mx-auto mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-5xl font-bold leading-tight mb-6 text-white">
            {title}
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-white max-w-3xl mx-auto">
            {description}
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left Column - Problems (3 columns) */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="mb-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-gradient-to-b from-red-500 to-red-600 rounded-full" />
                <h3 className="text-xl font-bold text-white">{problemTitle}</h3>
              </div>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
              variants={containerAnim}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {problemItems.map((item, index) => (
                <motion.div
                  key={index}
                  className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 hover:border-red-400/50 transition-all duration-300 hover:bg-white/10 hover:scale-[1.02]"
                  variants={itemAnim}
                >
                  <div className="flex gap-3 items-start">
                    <div className="min-w-[24px] mt-0.5">
                      <svg
                        className="w-5 h-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ color: '#ef4444' }}
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                      </svg>
                    </div>
                    <p className="text-sm leading-relaxed text-white m-0">{item}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Right Column - Solution (2 columns) */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              viewport={{ once: true }}
              className="h-full"
            >
              <div className="bg-gradient-to-br from-cyan-500/10 to-blue-600/10 backdrop-blur-sm rounded-2xl p-8 border border-cyan-400/20 h-full">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-blue-600 rounded-full" />
                  <h3 className="text-xl font-bold text-white">{solutionTitle}</h3>
                </div>

                <div className="space-y-6">
                  <p className="text-md leading-relaxed text-white">
                    {solutionDescription}
                  </p>

                  <div className="space-y-3">
                    {solutionPoints.map((point, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                        viewport={{ once: true }}
                        className="flex items-start gap-3 bg-white/5 rounded-lg p-3 hover:bg-white/10 transition-all duration-300"
                      >
                        <svg
                          className="w-5 h-5 min-w-[20px] mt-0.5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ color: '#00c6ff' }}
                        >
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                        <span className="text-md text-white">{point}</span>
                      </motion.div>
                    ))}
                  </div>

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    <button className="w-full bg-gradient-to-r from-cyan-400 to-blue-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-[0_10px_30px_rgba(0,198,255,0.3)]">
                      {ctaText}
                    </button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechRecruitingAgency;
